import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import { Modal } from '@shared/components/common/modal/modal';
import { InputFieldComponent } from '@shared/components/form/input/input-field.component';
import { LabelComponent } from '@shared/components/form/label/label.component';
import { SelectFieldComponent } from '@shared/components/form/select/select-field.component';
import { CreationButtons } from '@shared/components/ui/creation-buttons/creation-buttons';
import { AppFormComponent } from '@shared/components/ui/list-components';
import { ToSelectOptionsPipe } from '@shared/pipes/to-select-options.pipe';
import { CombosService } from '@shared/services/combos.service';
import { ErrorService } from '@shared/services/error.service';
import {
  Ciudad,
  Departamento,
  Pais,
} from '@shared/services/interfaces/combos.interface';
import { SnackbarService } from '@shared/services/snackbar.service';

import { Zona } from '../zonas.interface';
import { ZonasService } from '../zonas.service';

@Component({
  selector: 'app-crear-zona-modal',
  imports: [
    HasPermissionDirective,
    AppFormComponent,
    CreationButtons,
    InputFieldComponent,
    LabelComponent,
    Modal,
    ReactiveFormsModule,
    SelectFieldComponent,
    ToSelectOptionsPipe,
  ],
  templateUrl: './crear-zona-modal.html',
})
export class CrearZonaModal implements OnInit {
  saveForm!: FormGroup;
  loading = false;

  paises: Pais[] = [];
  departamentos: Departamento[] = [];
  ciudades: Ciudad[] = [];

  private formBuilder = inject(FormBuilder);
  private combosService = inject(CombosService);
  private zonasService = inject(ZonasService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogRef = inject(MatDialogRef<CrearZonaModal, Zona | undefined>);

  ngOnInit(): void {
    this.makeForm();
    this.loadPaises();
  }

  private makeForm(): void {
    this.saveForm = this.formBuilder.group({
      nombre: ['', Validators.required],
      ubi_pais_id: ['', Validators.required],
      ubi_departamento_id: ['', Validators.required],
      ubi_ciudad_id: ['', Validators.required],
    });

    this.saveForm.get('ubi_pais_id')?.valueChanges.subscribe((paisId) => {
      this.departamentos = [];
      this.ciudades = [];
      this.saveForm.patchValue(
        {
          ubi_departamento_id: '',
          ubi_ciudad_id: '',
        },
        { emitEvent: false },
      );

      if (paisId) {
        this.loadDepartamentos(Number(paisId));
      }
    });

    this.saveForm.get('ubi_departamento_id')?.valueChanges.subscribe((departamentoId) => {
      this.ciudades = [];
      this.saveForm.patchValue({ ubi_ciudad_id: '' }, { emitEvent: false });

      if (departamentoId) {
        this.loadCiudades(Number(departamentoId));
      }
    });
  }

  private loadPaises(): void {
    this.combosService.getPaisesCombo().subscribe({
      next: (response) => {
        if (response.status) {
          this.paises = response.data;
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    });
  }

  private loadDepartamentos(paisId: number): void {
    this.combosService.getDepartamentosCombo(paisId).subscribe({
      next: (response) => {
        if (response.status) {
          this.departamentos = response.data;
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    });
  }

  private loadCiudades(departamentoId: number): void {
    this.combosService.getCiudadesCombo(departamentoId).subscribe({
      next: (response) => {
        if (response.status) {
          this.ciudades = response.data;
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    });
  }

  saveZona(): void {
    if (this.saveForm.invalid) {
      this.saveForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.zonasService
      .store({
        nombre: this.saveForm.value.nombre,
        ubi_ciudad_id: Number(this.saveForm.value.ubi_ciudad_id),
      })
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.snackbarService.showSnackBar('Zona creada correctamente');
            this.dialogRef.close(response.data);
          }
        },
        error: (error) => {
          this.errorService.handleError(error);
        },
      })
      .add(() => {
        this.loading = false;
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
